import { fetchProductReviews } from "../../utils/fetchProductReviews";
import { useStaticQuery, graphql } from "gatsby";
import { getShopifyData } from "../../lib/dynamicQueries/config";

const COUNTRY_CODE = process.env.GATSBY_LOCALE_COUNTRY;
const CLUB_ACTIVE = process.env.GATSBY_CLUB_ACTIVE;

async function getPricing(product) {
  const cacheKey = `${product.handle}_${COUNTRY_CODE}`;

  // Check if a cached response exists in localStorage and is not expired
  const cachedData = localStorage.getItem(cacheKey);
  if (cachedData) {
    const parsedData = JSON.parse(cachedData);
    if (parsedData.expiry > new Date().getTime()) {
      return JSON.parse(parsedData.data);
    }
  }
  try {
    const response = await fetch(
      `https://bushbuck-api.netlify.app/.netlify/functions/getContextualPricing?handle=${product.handle}&country_code=${COUNTRY_CODE}`
    );

    const data = await response.json();

    if (Object.keys(data).length > 0) {
      const variantsResponse = await getVariantsWithPricing(
        product.variants,
        data?.data?.productByHandle.variants.nodes
      );

      const expiryDate = new Date().getTime() + 24 * 60 * 60 * 1000; // One day expiry
      const cachedData = {
        expiry: expiryDate,
        data: JSON.stringify(variantsResponse),
      };
      localStorage.setItem(cacheKey, JSON.stringify(cachedData));

      return variantsResponse;
    }
  } catch (error) {
    console.log("Error occurred:", error);
  }

  // Retry after 2 seconds if the API call fails or the data is empty
  await new Promise((resolve) => setTimeout(resolve, 2000));
  return getPricing(product);
}

async function getReviews(productId, page, stars, perPage) {
  if (stars > 0) {
    const response = await fetchProductReviews(
      productId.replace("gid://shopify/Product/", ""),
      page,
      stars,
      perPage
    );
    return response;
  } else {
    const response = await fetchProductReviews(
      productId.replace("gid://shopify/Product/", ""),
      page,
      undefined,
      perPage
    );
    return response;
  }
}

async function getVariantsWithPricing(variants, contextualVariants) {
  let mergedArray = [];

  for (let i = 0; i < variants.length; i++) {
    let found = false;

    for (let j = 0; j < contextualVariants?.length; j++) {
      if (variants[i].shopifyId === contextualVariants[j].shopifyId) {
        mergedArray.push({
          ...variants[i],
          contextualPricing: contextualVariants[j].contextualPricing,
        });

        found = true;
        break;
      }
    }

    if (!found) {
      mergedArray.push(variants[i]);
    }
  }

  return mergedArray;
}

export async function getVariantsById(variantIds) {
  const query = `query productVariants($ids: [ID!]!) {
    nodes(ids: $ids) {
    ...on ProductVariant {
        id
        title
        selectedOptions {
          name
          value 
        }
        quantityAvailable
        availableForSale
        product {
          id
          handle
          title
        }
        compareAtPrice {
          amount
        }
        image {
          url
        }
      }
    }
  }`;
  const response = await getShopifyData(query, {
    ids: variantIds,
  });

  return response?.data?.nodes ?? [];
}

export async function getVariants(handle) {
  const query = `query getProductByHandle($handle: String!) @inContext(country: ${COUNTRY_CODE}) {
    product(handle: $handle) {
      images (first: 50) {
        nodes {
          __typename
          originalSrc
          altText
        }
      }
      clubPrice: metafield(namespace: "custom", key: "${CLUB_ACTIVE}") {
        value
      }
      variants (first: 250){
        nodes {
            shopifyId:id
            title
            sku
            unpublished: metafield(namespace: "custom", key: "hidden_${COUNTRY_CODE.toLowerCase()}") {
              value
            }
            selectedOptions {
              name
              value
            }
            quantityAvailable
            availableForSale
            title
            image {
              originalSrc
              altText
            }
            price {
              amount
            }
            compareAtPrice {
              amount
            }
            clubPrice: metafield(namespace: "custom", key: "${CLUB_ACTIVE}") {
              value
            }
          }
      }
    }
    }`;
  const response = await getShopifyData(query, {
    handle: handle,
  });

  return response?.data?.product;
}

export async function getProductsById(productIds) {
  const query = `query products($ids: [ID!]!) @inContext(country: ${COUNTRY_CODE}) {
    nodes(ids: $ids) {
      ...on Product {
        id
        title
        handle
        options {
          name
          values
        }
        variants (first: 250){
          edges {
            node {
              id
              title
              sku
              unpublished: metafield(namespace: "custom", key: "hidden_${COUNTRY_CODE.toLowerCase()}") {
                value
              }
              selectedOptions {
                name
                value
              }
              product {
                handle
                title
              }
              quantityAvailable
              availableForSale
              title
              image {
                url
                altText
              }
              price {
                amount
              }
              compareAtPrice {
                amount
              }
            }
          }
        }
      }
    }
    }`;
  const response = await getShopifyData(query, {
    ids: productIds,
  });

  return response?.data?.nodes ?? [];
}


export async function getProductByHandle(handle) {
  const query = `query getProductByHandle($handle: String!) @inContext(country: ${COUNTRY_CODE}) {
    product(handle: $handle) {
      id
      title
      description
      handle
      options {
        name
        values
      }
      images (first: 50) {
        nodes {
          __typename
          url
          altText
        }
      }
      variants (first: 250){
        nodes {
            id
            title
            sku
            unpublished: metafield(namespace: "custom", key: "hidden_${COUNTRY_CODE.toLowerCase()}") {
              value
            }
            selectedOptions {
              name
              value
            }
            product {
              handle
              title
            }
            quantityAvailable
            availableForSale
            title
            image {
              url
              altText
            }
            price {
              amount
            }
            compareAtPrice {
              amount
            }
          }
      }
    }
    }`;
  const response = await getShopifyData(query, {
    handle
  });

  return response?.data?.product ?? null;
}

function getShopifyProductByHandle(productId) {
  const { shopifyProduct, contentfulProduct } = useStaticQuery(graphql`
    query {
      contentfulProduct(slug: { eq: "vertex-fleece-tee" }) {
        fitType
        sizeGuide {
          title
          url
        }
      }
      shopifyProduct(handle: { eq: "vertex-fleece-tee" }) {
        id
        title
        handle
        vendor
        options {
          name
          values
        }
        clubNZ: metafield(namespace: "custom", key: "club_nz") {
          value
        }
        clubAU: metafield(namespace: "custom", key: "club_au") {
          value
        }
        clubUS: metafield(namespace: "custom", key: "club_us") {
          value
        }
        clubUK: metafield(namespace: "custom", key: "club_uk") {
          value
        }
        clubPT: metafield(namespace: "custom", key: "club_pt") {
          value
        }
        metafield(namespace: "custom", key: "club_nz") {
          value
        }
        media {
          preview {
            image {
              originalSrc
              gatsbyImageData(
                layout: FULL_WIDTH
                formats: WEBP
                placeholder: DOMINANT_COLOR
              )
              altText
            }
          }
        }
        variants {
          id
          shopifyId
          sku
          selectedOptions {
            name
            value
          }
          availableForSale
          title
          image {
            gatsbyImageData(
              layout: FULL_WIDTH
              formats: WEBP
              placeholder: DOMINANT_COLOR
              breakpoints: 500
            )
            altText
          }
          price
          compareAtPrice
          weight
          weightUnit
        }
      }
    }
  `);

  return { ...contentfulProduct, ...shopifyProduct };
}

export {
  getPricing,
  getReviews,
  getVariantsWithPricing,
  getShopifyProductByHandle,
};
